// Estado inicial del header (fondo + color) resuelto EN RENDER a partir de los
// segmentos de ruta activos y de una tabla mínima de datos del CMS.
//
// Por qué existe: el Header vive en el root layout y se renderiza antes que la
// página, así que no puede saber qué hero va a montar la página. Si el estado
// inicial se fija en un useEffect del hero, el HTML SSR llega con el header
// equivocado y el cambio se anima después del primer paint (parpadeo). Aquí
// reproducimos la decisión que toman los heros al montar, para que SSR y primer
// render del cliente ya sean correctos. Si esta tabla se desincroniza del hero,
// el hero sigue corrigiendo el estado al montar (vuelve el parpadeo, nada se rompe).
//
// Regla de seguridad: ante cualquier duda (datos ausentes, slug desconocido → 404)
// devolvemos el estado por defecto (fondo blanco, texto negro). Un header
// transparente sobre una página sin hero sería invisible y nadie lo corregiría.

export type HeaderColor = 'white' | 'black'

export interface HeaderState {
  background: boolean
  color: HeaderColor
}

export interface ChronicleHeaderData {
  slug: string | null
  activateHero: boolean | null
  colored: boolean | null
  color: string | null
  headerColor: string | null
}

export interface HeaderStatesData {
  projects: Array<{ slug: string | null; headerColor: string | null }> | null
  chronicles: ChronicleHeaderData[] | null
  fabrica: Omit<ChronicleHeaderData, 'slug'> | null
}

export const HEADER_DEFAULT: HeaderState = { background: true, color: 'black' }
export const HEADER_OVER_HERO: HeaderState = { background: false, color: 'white' }

function normalizeColor(value: string | null | undefined): HeaderColor {
  return value === 'black' ? 'black' : 'white'
}

// Misma lógica que components/modulesJournal/hero (IntersectionObserver → hero visible)
function resolveChronicleHero(data: Omit<ChronicleHeaderData, 'slug'>): HeaderState {
  if (data.activateHero !== true) return HEADER_DEFAULT
  if (data.colored && data.color) {
    // Hero de color plano: texto negro salvo que el editor haya elegido otro color
    return { background: false, color: data.headerColor && data.headerColor !== 'white' ? normalizeColor(data.headerColor) : 'black' }
  }
  return { background: false, color: normalizeColor(data.headerColor) }
}

export function resolveInitialHeaderState(segments: string[], data: HeaderStatesData | null): HeaderState {
  // Home: hero a pantalla completa siempre (hero.home.headerColor por defecto "white")
  if (segments.length === 0) return HEADER_OVER_HERO

  const [section, slug] = segments

  if (section === 'projects' && slug) {
    const project = data?.projects?.find((p) => p.slug === slug)
    if (!project) return HEADER_DEFAULT
    // components/modulesProjects/hero: siempre transparente, color = hero.headerColor
    return { background: false, color: normalizeColor(project.headerColor) }
  }

  if (section === 'journal' && slug) {
    const chronicle = data?.chronicles?.find((c) => c.slug === slug)
    if (!chronicle) return HEADER_DEFAULT
    return resolveChronicleHero(chronicle)
  }

  if (section === 'la-fabrica') {
    if (!data?.fabrica) return HEADER_DEFAULT
    return resolveChronicleHero(data.fabrica)
  }

  return HEADER_DEFAULT
}
